import { useCallback, useRef, useState } from 'react';
import api from '../services/api';

export interface TechAvailability {
  technician_id: number;
  name: string;
  status: 'free' | 'busy' | 'off' | 'booked_out';
  busy_until: string | null;
  workload_percent: number;
  work_window: { start: string; end: string } | null;
  appointment_count: number;
}

/**
 * Fetches the per-technician day-availability summary
 * (GET /api/admin/appointments/tech-availability-summary) lazily — only when `load()`
 * is first called (e.g. on dropdown open) — and only once per hook instance.
 * Degrades quietly on error and allows retry on the next `load()` call.
 */
export function useTechAvailabilitySummary(date?: string) {
  const [byTechId, setByTechId] = useState<Record<number, TechAvailability>>({});
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  const load = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    api
      .get('/appointments/tech-availability-summary', { params: date ? { date } : {} })
      .then((res) => {
        const map: Record<number, TechAvailability> = {};
        for (const t of res.data?.technicians ?? []) map[t.technician_id] = t;
        setByTechId(map);
      })
      .catch(() => {
        fetchedRef.current = false; // allow retry on next open
      })
      .finally(() => setLoading(false));
  }, [date]);

  return { byTechId, loading, load };
}
